import { useEffect, useState } from 'react';
import type { Tariff } from '../../api/client';
import { getCachedTariffs, refreshTariffs } from '../../api/tariffsCache';
import { translateApiError } from '../lib/errors';

function formatPrice(amount: number | string): string {
    const value = Number(amount);

    if (Number.isNaN(value)) return String(amount);

    return value.toLocaleString('ru-RU', {
        minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
        maximumFractionDigits: 2,
    });
}

/** Количество песен в тарифе: GENERATION + GENERATION_VOCAL (TRACK не считаем). */
function getSongsCount(tariff: Tariff): number {
    return tariff.items.reduce(
        (sum, item) =>
            item.type === 'GENERATION' || item.type === 'GENERATION_VOCAL'
                ? sum + item.amount
                : sum,
        0,
    );
}

const SONG_FORMS: Record<string, string> = {
    one: 'песня',
    few: 'песни',
    many: 'песен',
    other: 'песен',
};
const ruPlural = new Intl.PluralRules('ru-RU');

function formatSongs(count: number): string {
    return `${count} ${SONG_FORMS[ruPlural.select(count)]}`;
}

function gradient(tariff: Tariff): string {
    const stops = [...tariff.appearance.buttonGradient.stops]
        .sort((a, b) => a.value - b.value)
        .map((stop) => `${stop.color} ${stop.value * 100}%`)
        .join(', ');

    return `linear-gradient(${tariff.appearance.buttonGradient.angle}deg, ${stops})`;
}

const PLACEHOLDER_CLASS =
    'rounded-2xl bg-gradient-to-r from-purple-50 to-pink-50 p-5 text-center text-sm text-gray-600';

export default function Pricing() {
    const tariffParams = { order: 'ASC' as const, field: 'price' as const, limit: 50, offset: 0 };
    const cached = getCachedTariffs(tariffParams);
    const [tariffs, setTariffs] = useState<Tariff[]>(() =>
        (cached?.tariffs ?? []).filter(
            (tariff) =>
                !tariff.items.some((item) => item.type === 'ADDITIONAL_GENERATION'),
        ),
    );
    const [loading, setLoading] = useState(!cached);
    const [error, setError] = useState('');

    useEffect(() => {
        let mounted = true;

        void refreshTariffs(tariffParams)
            .then((response) => {
                if (!mounted) return;
                setTariffs(
                    response.tariffs.filter(
                        (tariff) =>
                            !tariff.items.some(
                                (item) => item.type === 'ADDITIONAL_GENERATION',
                            ),
                    ),
                );
                setError('');
            })
            .catch((requestError) => {
                if (mounted && !cached) setError(translateApiError(requestError));
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, []);

    return (
        <div className="prose prose-sm max-w-none">
            <h1 className="text-2xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Цены
            </h1>

            <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                    Стоимость услуг
                </h2>

                {loading && (
                    <div className={PLACEHOLDER_CLASS}>Загружаем тарифы…</div>
                )}

                {!loading && error && (
                    <div
                        role="alert"
                        className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700"
                    >
                        {error}
                    </div>
                )}

                {!loading && !error && !tariffs.length && (
                    <div className={PLACEHOLDER_CLASS}>
                        Сейчас нет доступных тарифов.
                    </div>
                )}

                {!loading && !error && tariffs.length > 0 && (
                    <div className="space-y-3">
                        {tariffs.map((tariff) => {
                            const songsCount = getSongsCount(tariff);

                            return (
                                <section
                                    key={tariff.id}
                                    className="rounded-2xl border-[1.5px] p-[18.5px]"
                                    style={{
                                        background: tariff.appearance.background,
                                        borderColor: tariff.appearance.border,
                                    }}
                                    aria-labelledby={`pricing-tariff-title-${tariff.id}`}
                                >
                                    <h3
                                        id={`pricing-tariff-title-${tariff.id}`}
                                        className="text-base leading-6 font-bold text-gray-900"
                                    >
                                        {tariff.name}
                                    </h3>

                                    <div className="mt-2 flex items-baseline gap-2">
                                        <span className="text-3xl leading-9 font-bold tracking-tight text-gray-900">
                                            {formatPrice(tariff.price.amount)} ₽
                                        </span>
                                        {songsCount > 0 && (
                                            <span className="text-base leading-6 text-gray-500">
                                                / {formatSongs(songsCount)}
                                            </span>
                                        )}
                                    </div>

                                    {tariff.shortDescription && (
                                        <p className="mt-1 text-sm leading-5 text-gray-500">
                                            {tariff.shortDescription}
                                        </p>
                                    )}
                                </section>
                            );
                        })}
                    </div>
                )}
            </section>

            <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                    Что входит в стоимость
                </h2>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li>Генерация двух вариантов музыкальной композиции</li>
                    <li>Возможность прослушать оба варианта перед покупкой</li>
                    <li>Скачивание выбранных треков в формате MP3 без водяных знаков</li>
                    <li>Неисключительные права на использование композиции</li>
                    <li>Возможность использования в коммерческих целях</li>
                    <li>Публикация на стриминговых платформах и в социальных сетях</li>
                </ul>
            </section>

            <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                    Бесплатная генерация
                </h2>
                <p className="text-gray-700 mb-2">
                    Каждому новому пользователю доступна 1 бесплатная генерация для
                    ознакомления с сервисом.
                </p>
                <p className="text-gray-700">
                    После использования бесплатной генерации вы можете приобрести
                    дополнительные генерации по актуальным тарифам сервиса.
                </p>
            </section>

            <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                    Как происходит оплата
                </h2>
                <ol className="list-decimal pl-6 text-gray-700 space-y-2">
                    <li>
                        Вы создаёте песню, указывая название, текст, стиль и тип вокала
                    </li>
                    <li>Нейросеть генерирует два варианта композиции</li>
                    <li>Вы прослушиваете оба варианта с водяными знаками бесплатно</li>
                    <li>Выбираете один или оба трека для покупки</li>
                    <li>
                        После оплаты получаете доступ к скачиванию выбранных треков без
                        водяных знаков
                    </li>
                </ol>
            </section>

            <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                    Способы оплаты
                </h2>
                <p className="text-gray-700 mb-2">
                    Мы принимаем оплату следующими способами:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                    <li>Банковские карты (Visa, Mastercard, МИР)</li>
                    <li>Электронные кошельки</li>
                </ul>
            </section>

            <section className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                    Часто задаваемые вопросы
                </h2>

                <div className="space-y-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            Можно ли вернуть деньги?
                        </h3>
                        <p className="text-gray-700">
                            Возврат денежных средств возможен в случае технической
                            невозможности предоставления услуги по вине сервиса или при
                            двойном списании средств. Подробнее в разделе "Публичная оферта".
                        </p>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            Что делать, если не понравился результат?
                        </h3>
                        <p className="text-gray-700">
                            Вы можете прослушать оба варианта композиции бесплатно перед
                            покупкой. Оплата требуется только для скачивания треков без
                            водяных знаков.
                        </p>
                    </div>
                </div>
            </section>

            <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                    Контактная информация
                </h2>
                <p className="text-gray-700">
                    По вопросам оплаты и тарификации обращайтесь: <a
                        href="mailto:sup_musiccreator@mail.ru"
                        className="text-purple-600 hover:underline"
                    >
                        sup_musiccreator@mail.ru</a>
                </p>
            </section>
        </div>
    );
}
